'use client';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import axios, { AxiosError } from 'axios';
import { Loader } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import contactLottie from '../../assets/lottie/contact.json';
import AnimationLottie from '../helpers/animation-lottie';
import { SendIcon } from '../icons/SendIcon';
import { GlowingEffect } from '../ui/glowing-effect';
const formSchema = z.object({
  name: z
    .string()
    .nonempty('Please fill your name')
    .min(2, { message: 'Name must be at least 2 characters.' }),

  email: z.email('Invalid email address').nonempty('Email is required'),
  message: z
    .string()
    .nonempty('Please fill your message')
    .min(10, { message: 'Message must be at least 10 characters.' }),
});

type FormData = z.infer<typeof formSchema>;

export default function Contact() {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);

    try {
      await axios.post('/api/contact', data);
      toast.success('Message sent successfully!');
      reset();
    } catch (error) {
      const err = error as AxiosError<{ error: string }>;
      const message = err.response?.data?.error || err.message || 'Something went wrong.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id='contact' className='relative z-50 my-20'>
      <div className='mb-10 text-center'>
        <h2 className='mb-2 text-3xl font-bold text-white'>Contact Me</h2>
        <p className='text-muted-foreground'>Let&apos;s work together or just say hi 👋</p>
      </div>

      <div className='grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16'>
        <div className='flex justify-center'>
          <div className='h-full w-full max-w-md'>
            <AnimationLottie animationPath={contactLottie} />
          </div>
        </div>

        <Card className='relative border border-[#25213b] bg-slate-950 text-white'>
          <GlowingEffect
            blur={0}
            borderWidth={3}
            spread={80}
            glow={true}
            disabled={false}
            proximity={64}
            inactiveZone={0.01}
          />
          <div className='absolute top-0 h-0.5 w-full bg-linear-to-r from-transparent via-violet-500 to-transparent' />
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-6 p-8'>
            <div>
              <label htmlFor='name' className='mb-1 block font-semibold'>
                Name
              </label>
              <Input
                id='name'
                {...register('name')}
                placeholder='Your name'
                autoComplete='off'
                className='border border-[#1f223c] bg-[#11152c] text-white focus-visible:ring-violet-500'
              />
              {errors.name && <p className='mt-1 text-sm text-red-500'>{errors.name.message}</p>}
            </div>
            <div>
              <label htmlFor='email' className='mb-1 block font-semibold'>
                Email
              </label>
              <Input
                id='email'
                {...register('email')}
                placeholder='you@example.com'
                autoComplete='off'
                className='border border-[#1f223c] bg-[#11152c] text-white focus-visible:ring-violet-500'
              />
              {errors.email && <p className='mt-1 text-sm text-red-500'>{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor='message' className='mb-1 block font-semibold'>
                Message
              </label>
              <Textarea
                id='message'
                {...register('message')}
                placeholder='Your message...'
                autoComplete='off'
                rows={5}
                className='resize-none border border-[#1f223c] bg-[#11152c] text-white focus-visible:ring-violet-500'
              />
              {errors.message && (
                <p className='mt-1 text-sm text-red-500'>{errors.message.message}</p>
              )}
            </div>
            <Button
              type='submit'
              disabled={loading}
              className='bg-linear-to-r from-violet-500 to-emerald-400 font-semibold text-white transition-all duration-300 hover:from-pink-500 hover:to-cyan-500'
            >
              {loading ? (
                <>
                  <Loader className='h-4 w-4 animate-spin' />
                  <span>Sending...</span>
                </>
              ) : (
                <span className='flex items-center gap-2'>
                  {' '}
                  <SendIcon /> <span>Send Message</span>
                </span>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
}
